import { createSerializationAdapter } from "@tanstack/react-router";
import { createMiddleware, createStart } from "@tanstack/react-start";

import { DatabaseError, FileSizeLimitError, ForbiddenError } from "./lib/errors";
import { sanitizeError } from "./routes/-middlewares/sanitize-error";

export const forbiddenErrorAdapter = createSerializationAdapter({
	key: "forbiddenError",
	test: (v): v is ForbiddenError => v instanceof ForbiddenError,
	toSerializable: (v) => {
		return {
			name: v.name,
			message: v.message,
			stack: v.stack,
		};
	},
	fromSerializable: (v) => new ForbiddenError(v.message),
});

export const databaseErrorAdapter = createSerializationAdapter({
	key: "databaseError",
	test: (v): v is DatabaseError => v instanceof DatabaseError,
	toSerializable: (v) => {
		return {
			name: v.name,
			message: v.message,
		};
	},
	fromSerializable: (v) => new DatabaseError(v.message),
});

export const fileSizeLimitErrorAdapter = createSerializationAdapter({
	key: "fileSizeLimitError",
	test: (v): v is FileSizeLimitError => v instanceof FileSizeLimitError,
	toSerializable: (v) => ({
		name: v.name,
		message: v.message,
	}),
	fromSerializable: (v) => new FileSizeLimitError(v.message),
});

/**
 * Global function middleware that catches, logs, and sanitizes errors
 * thrown by any server function. Prevents raw DB / internal details
 * from leaking to the client while ensuring server-side visibility.
 */
const errorLoggingMiddleware = createMiddleware({
	type: "function",
}).server(async ({ next }) => {
	try {
		return await next();
	} catch (error) {
		throw sanitizeError(error);
	}
});

export const startInstance = createStart(() => {
	return {
		serializationAdapters: [forbiddenErrorAdapter, fileSizeLimitErrorAdapter],
		functionMiddleware: [errorLoggingMiddleware],
	};
});
